/**
 * 調整用の数値（docs/01_game-design.md、docs/03_simulation.md）。
 * 地形の種類ごとの配列は core/world.ts の Biome の番号順：
 * Lake, Road, Meadow, Forest, Wetland, Scree, Rock, Snow, Cliff
 */

/** Tobler 関数に掛ける基本係数（トップ選手の補正） */
export const HIKE_BASE_FACTOR = 1.25;

/** 上り（勾配 5% 超）にさらに掛ける係数。草原の急な上りで鉛直 約 930m/h（X-Alps の選手並み） */
export const HIKE_UPHILL_FACTOR = 1.4;
/** 下り（勾配 −10% より急）にさらに掛ける係数。急な下りで鉛直 約 1000m/h（同じ勾配なら登りより少し速い） */
export const HIKE_DOWNHILL_FACTOR = 1.1;

/** 勾配による係数（上り・下りの補正）。gen の経路づくりと sim の移動の両方で使う */
export function hikeGradeFactor(grade: number): number {
  if (grade > 0.05) return HIKE_UPHILL_FACTOR;
  if (grade < -0.1) return HIKE_DOWNHILL_FACTOR;
  return 1;
}

/** 地形ごとの歩行速度の倍率（0 は通行不可） */
export const BIOME_HIKE_SPEED: readonly number[] = [0, 1.15, 1.0, 0.9, 0.75, 0.75, 0.7, 0.7, 0];

/** 地形ごとのスタミナ消費の倍率 */
export const BIOME_HIKE_STAMINA: readonly number[] = [0, 0.9, 1.0, 1.1, 1.4, 1.5, 1.6, 1.5, 0];

/** ゲーム開始の時刻（その日の 00:00 からの秒） */
export const DAY_START_S = 7 * 3600;

/** 時間（docs/01 3 章、docs/02 7 章） */
export const TIME = {
  dayStartS: DAY_START_S,
  sunsetS: 20.5 * 3600,
  bivouacS: 21 * 3600,
  /** 通常の時間倍率（ゲーム内 1 時間 ≈ 11 秒） */
  baseScale: 330,
  editSlow: 0.25,
  /** 刻み（ゲーム内秒） */
  tickS: 0.25,
  hikeStepS: 1,
  weatherStepS: 10,
  standingsStepS: 60,
  /** 1 回の advance で処理する刻みの上限（処理落ち対策） */
  maxTicksPerAdvance: 4000,
} as const;

/** 能力値の補正の幅（docs/03 3 章） */
export const STAT_RANGE = {
  uphill: 0.1,
  staminaUse: 0.15,
  nightRecovery: 0.1,
  flatDown: 0.12,
  downStamina: 0.2,
  sink: 0.06,
  prepPack: 0.2,
  gustWindow: 0.15,
} as const;

export const STAMINA = {
  hikePerHour: 2.0,
  per100mUp: 0.6,
  per100mDown: 0.3,
  flightRecoverPerHour: 3,
  crash: 30,
  nightRecoverRatio: 0.7,
  lowThreshold: 30,
  lowFactor: 0.85,
  veryLowThreshold: 10,
  veryLowFactor: 0.7,
  rainFactor: 1.2,
} as const;

/** 雨（雷雲の下）の歩行速度の補正。メンタルで最大半分まで軽減 */
export const RAIN_HIKE_PENALTY = 0.15;

export const LAUNCH = {
  prepS: 12 * 60,
  /** 離陸できる最大風速（km/h）。これを超えるときだけ風待ちになる。グライダースキル 1〜10 で補間 */
  maxWindKmh: [28, 34] as const,
  /** これ未満なら向きを問わず、普通に離陸できる */
  calmKmh: 15,
  /** 離陸の向きと風上のずれがこれ以内なら向かい風（普通に離陸できる） */
  maxHeadAngleDeg: 70,
  /**
   * 追い風・横風の離陸：禁止はせず、失敗しやすくする（フライトは「リスクはあってもリターンが大きい」）。
   * 失敗の確率 = base + perKmh × (風速 − calmKmh)。失敗すると lossS のロスとスタミナ −stamina。
   */
  tailwind: { base: 0.2, perKmh: 0.02, maxP: 0.5, lossS: 300, stamina: 2 },
  /** 最大風速のこの割合を超えると離陸失敗がありうる */
  failFrom: 0.85,
  failMaxP: 0.3,
  failLossS: 600,
  failStamina: 3,
  windCheckS: 60,
  /** 強風の風待ちの上限。これを超えると離陸を断念する（強制ポーズで組み直す） */
  windWaitMaxS: 20 * 60,
  /** 日没の何秒前まで離陸できるか */
  latestBeforeSunsetS: 0,
} as const;

export const PACK = {
  normalS: 600,
  roughS: 1200,
  treeS: 2700,
  treeChance: 0.4,
} as const;

/** フライトの物理（docs/03 8 章） */
export const FLIGHT = {
  /** 速度入力 s に対する対気速度（km/h）と沈下（m/s） */
  // 競技用のクロスカントリー機（通常速度で滑空比 約 10.5）
  polar: [
    { s: -0.85, kmh: 23, sink: 1.45 },
    { s: -0.3, kmh: 30, sink: 0.85 },
    { s: 0, kmh: 38, sink: 1.0 },
    { s: 1, kmh: 54, sink: 1.85 },
  ],
  stallKmh: 23,
  speedTauS: 1.5,
  bankTauS: 0.8,
  inputTauS: 0.25,
  maxBankDeg: 45,
  assistBankDeg: 40,
  crashSinkMs: 4,
  stallSinkMs: 6,
  deepStallSinkMs: 10,
  stallRecoverS: 3,
  deepStallS: [3, 5] as const,
  /** 予定外の着陸とみなす、目標からの距離 */
  unplannedLandingM: 1000,
  landingAssistAglM: 150,
  /** ビッグイヤーで増える沈下 */
  bigEarsSinkMs: 2.5,
  /** これより長く飛んでいたら着陸に入る（安全装置） */
  maxFlightS: 5 * 3600,
} as const;

export const GUST = {
  baseRatePerMin: 0.1,
  refWindKmh: 20,
  leeMult: 3,
  edgeMult: 1.5,
  stormMult: 4,
  stormRangeM: 10_000,
  sideRatio: 0.7,
  windowS: 5,
  strongWindKmh: 30,
  autoSuccess: [0.7, 0.95] as const,
} as const;

export const LUCK = { maxReroll: 0.2 } as const;

export const COURSE_RULES = {
  /** CP は地上で通過する（本家 X-Alps のターンポイントのサインと同じ）。空中で上を通っても通過にならない */
  cpOnFoot: true,
  cpRadiusM: 300,
  goalRadiusM: 150,
  progressLogStepM: 100,
} as const;

/** 臨時ノードを作らずに既存ノードへ寄せる距離 */
export const SNAP_TO_NODE_M = 300;
